import { useRef, useState } from 'react';
import { placeImage, type ImagePlacement, type Rect } from '@cal/engine';
import { Modal, NumberField, Seg } from '../ui/controls';
import { previewUrl } from '../assets';

/**
 * Crop/zoom/pan editor. Shows the exact visible crop (inside the frame) and the
 * rest of the photo dimmed. Uses the same placement maths as the export.
 */
export function CropEditor({ frame, radius, meta, placement, onChange, onClose }: {
  frame: Rect; radius: number; meta: { width: number; height: number }; placement: ImagePlacement; onChange: (p: Partial<ImagePlacement>) => void; onClose: () => void;
}) {
  const stageW = 560;
  const scale = Math.min(stageW / frame.w, 380 / frame.h) * 0.62;
  const fw = frame.w * scale, fh = frame.h * scale;
  const W = stageW, H = Math.max(fh * 1.6, 260);
  const fx = (W - fw) / 2, fy = (H - fh) / 2;
  const f0 = { x: 0, y: 0, w: frame.w, h: frame.h };
  const img = placeImage(f0, meta, placement);
  const drag = useRef<{ x: number; y: number; p: ImagePlacement } | null>(null);
  const [p, setP] = useState(placement);
  const cur = placeImage(f0, meta, p);
  const apply = (np: ImagePlacement) => { setP(np); onChange({ zoom: np.zoom, panX: np.panX, panY: np.panY, fit: np.fit }); };
  void img;
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dxMm = (e.clientX - d.x) / scale, dyMm = (e.clientY - d.y) / scale;
    const base = placeImage(f0, meta, d.p);
    const rangeX = (frame.w - base.w) / 2, rangeY = (frame.h - base.h) / 2;
    const clamp = (v: number) => Math.max(-1, Math.min(1, v));
    apply({ ...d.p, panX: Math.abs(rangeX) < 1e-6 ? 0 : clamp(d.p.panX + dxMm / rangeX), panY: Math.abs(rangeY) < 1e-6 ? 0 : clamp(d.p.panY + dyMm / rangeY) });
  };
  return (
    <Modal title="Crop & position photograph" onClose={onClose} wide footer={<><button className="btn" onClick={() => apply({ ...p, zoom: 1, panX: 0, panY: 0 })}>Reset</button><button className="btn primary" onClick={onClose}>Done</button></>}>
      <p className="small muted">Drag the photo to position it. The bright area is exactly what will print in the frame (plus bleed where the frame touches the page edge).</p>
      <div className="crop-stage" style={{ width: W, height: H, maxWidth: '100%', cursor: 'grab' }}
        onPointerDown={(e) => { (e.target as Element).setPointerCapture(e.pointerId); drag.current = { x: e.clientX, y: e.clientY, p }; }}
        onPointerMove={onMove} onPointerUp={() => (drag.current = null)}>
        <svg width={W} height={H} style={{ display: 'block' }}>
          <defs>
            <clipPath id="crop-frame"><rect x={fx} y={fy} width={fw} height={fh} rx={radius * scale} /></clipPath>
          </defs>
          <image href={previewUrl((placement as ImagePlacement).assetId)} x={fx + cur.x * scale} y={fy + cur.y * scale} width={cur.w * scale} height={cur.h * scale} preserveAspectRatio="none" opacity={0.3} />
          <image href={previewUrl(placement.assetId)} x={fx + cur.x * scale} y={fy + cur.y * scale} width={cur.w * scale} height={cur.h * scale} preserveAspectRatio="none" clipPath="url(#crop-frame)" />
          <rect x={fx} y={fy} width={fw} height={fh} rx={radius * scale} fill="none" stroke="#fff" strokeWidth={1.5} />
        </svg>
      </div>
      <div className="row wrap" style={{ alignItems: 'flex-end' }}>
        <Seg label="Fit" value={p.fit} onChange={(v) => apply({ ...p, fit: v, zoom: 1, panX: 0, panY: 0 })} options={[{ value: 'fill', label: 'Fill frame' }, { value: 'fit', label: 'Fit whole photo' }]} />
        <label className="field grow">
          <span className="lbl">Zoom {Math.round(p.zoom * 100)}%</span>
          <input type="range" min={p.fit === 'fit' ? 0.3 : 1} max={4} step={0.01} value={p.zoom} onChange={(e) => apply({ ...p, zoom: +e.target.value })} aria-label="Zoom" />
        </label>
        <div style={{ width: 100 }}><NumberField label="Pan X" value={p.panX} min={-1} max={1} step={0.05} onChange={(v) => apply({ ...p, panX: v })} /></div>
        <div style={{ width: 100 }}><NumberField label="Pan Y" value={p.panY} min={-1} max={1} step={0.05} onChange={(v) => apply({ ...p, panY: v })} /></div>
      </div>
    </Modal>
  );
}
